import React, { RefObject } from 'react';

import { Attributes } from '@lumapps/data-attributes';
import { mdiCheck, mdiContentCopy } from '@lumapps/lumx/icons';
import { ButtonSize, IconButton } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

export interface CopyToClipboardButtonProps extends Attributes {
    /** value to copy to clipboard */
    value: string;
    /** icon size */
    size?: ButtonSize;
    /** custom classname */
    className?: string;
    /** Callback called once the text has been copied */
    onSuccess?: () => void;
    label?: string;
    buttonRef?: RefObject<HTMLButtonElement>;
    /** Callback on click */
    onClick?: () => void;
}

/**
 * Component that displays a button to be displayed besides a text so that the text can be copied to the clipboard
 *
 * @family Buttons
 * @param CopyToClipboardButtonProps
 * @returns CopyToClipboardButton
 */
export const CopyToClipboardButton: React.FC<CopyToClipboardButtonProps> = ({
    value,
    onSuccess,
    label,
    size = 's',
    buttonRef,
    onClick,
    ...props
}) => {
    const { translateKey } = useTranslate();

    const [isCopied, setIsCopied] = React.useState(false);

    const copyValue = () => {
        if (onClick) {
            onClick();
        }
        navigator.clipboard.writeText(value);
        setIsCopied(true);
        if (onSuccess) {
            onSuccess();
        }
        setTimeout(() => {
            setIsCopied(false);
        }, 1000);
    };

    return (
        <IconButton
            label={label || translateKey(isCopied ? GLOBAL.COPIED : GLOBAL.COPY)}
            icon={isCopied ? mdiCheck : mdiContentCopy}
            color={isCopied ? 'green' : undefined}
            onClick={copyValue}
            size={size}
            emphasis="low"
            ref={buttonRef}
            {...props}
        />
    );
};
